import { ApiResponse, LoginCredentials, LoginSuccessResponse, RegisterTenantPayload } from '@saas/shared-types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

class ApiClient {
  private activeSubdomain: string = 'acme';
  private accessToken: string | null = null;

  constructor() {
    this.accessToken = localStorage.getItem('saas_access_token');
    const storedSubdomain = localStorage.getItem('saas_tenant_subdomain');
    if (storedSubdomain) {
      this.activeSubdomain = storedSubdomain;
    }
  }

  public setTenantSubdomain(subdomain: string): void {
    this.activeSubdomain = subdomain.trim().toLowerCase();
    localStorage.setItem('saas_tenant_subdomain', this.activeSubdomain);
  }

  public getTenantSubdomain(): string {
    return this.activeSubdomain;
  }

  public setAccessToken(token: string | null): void {
    this.accessToken = token;
    if (token) {
      localStorage.setItem('saas_access_token', token);
    } else {
      localStorage.removeItem('saas_access_token');
    }
  }

  public getAccessToken(): string | null {
    return this.accessToken || localStorage.getItem('saas_access_token');
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'x-tenant-subdomain': this.activeSubdomain,
      'x-tenant-id': this.activeSubdomain,
      ...(options.headers as Record<string, string>),
    };

    const token = this.getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    const data: ApiResponse<T> = await response.json().catch(() => ({
      success: false,
      error: { code: 'PARSE_ERROR', message: 'Failed to parse server response.' },
    }));

    if (!response.ok || !data.success) {
      const errorMessage = (data as any)?.error?.message || `Request failed with status ${response.status}`;
      throw new Error(errorMessage);
    }

    return (data as any).data;
  }

  // Auth Endpoints
  public async login(credentials: LoginCredentials): Promise<LoginSuccessResponse> {
    const data = await this.request<LoginSuccessResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    this.setAccessToken(data.tokens.accessToken);
    localStorage.setItem('saas_refresh_token', data.tokens.refreshToken);
    return data;
  }

  public async registerTenant(payload: RegisterTenantPayload): Promise<any> {
    return this.request('/auth/register-tenant', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  public async getMe(): Promise<any> {
    return this.request('/auth/me', { method: 'GET' });
  }

  // Tenant Public & Protected Endpoints
  public async getPublicTenantInfo(subdomain?: string): Promise<any> {
    const targetSubdomain = subdomain || this.activeSubdomain;
    return this.request(`/tenant/public-info?subdomain=${encodeURIComponent(targetSubdomain)}`, {
      method: 'GET',
    });
  }

  public async getDashboardData(): Promise<any> {
    return this.request('/tenant/dashboard-data', { method: 'GET' });
  }

  public async createRecord(payload: { title: string; category?: string; data?: any }): Promise<any> {
    return this.request('/tenant/records', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // Central Analytics Endpoint
  public async getGlobalAnalytics(): Promise<any> {
    return this.request('/analytics/global-overview', { method: 'GET' });
  }
}

export const api = new ApiClient();
